import React, { useState } from 'react';
import { Form, Input, DatePicker, Button, Typography, Select, Space } from 'antd';
import { ArrowRightOutlined, DownOutlined, RightOutlined, EnvironmentOutlined, PictureOutlined, PlayCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import SimpleImageDropzone from './SimpleImageDropzone';
import { videoUrlValidator } from '../../../utils/videoUtils';

const { TextArea } = Input;
const { Title, Text } = Typography;

const WizardAuctionStep = ({ form, categories, selectedCategoryId, onSelectCategory, loading, onNext, photos, onPhotosChange }) => {
    const [showMore, setShowMore] = useState(false);

    const disabledDate = (current) => current && current < moment().startOf('day');

    const disabledTime = (current) => {
        if (!current || current.format('YYYY-MM-DD') !== moment().format('YYYY-MM-DD')) {
            return {};
        }
        const now = moment();
        return {
            disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
            disabledMinutes: (hour) =>
                hour === now.hour() ? Array.from({ length: now.minute() + 1 }, (_, i) => i) : []
        };
    };

    return (
        <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: 4 }}>
                🐄 Step 1: Tell us about your auction
            </Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 32, fontSize: 16 }}>
                Just a few simple questions
            </Text>

            <Form form={form} layout="vertical" requiredMark={false} onFinish={onNext}>
                <Form.Item
                    name="title"
                    label={<span style={{ fontSize: 17, fontWeight: 600 }}>1️⃣ What are you selling?</span>}
                    rules={[{ required: true, message: 'Please type a title' }]}
                >
                    <Input
                        size="large"
                        placeholder="e.g. 5 Healthy Goats"
                        style={{ fontSize: 16, height: 52, borderRadius: 12 }}
                    />
                </Form.Item>

                <Form.Item
                    name="description"
                    label={<span style={{ fontSize: 17, fontWeight: 600 }}>2️⃣ Tell people about it</span>}
                    rules={[{ required: true, message: 'Please write a short description' }]}
                >
                    <TextArea
                        rows={3}
                        placeholder="e.g. Healthy goats, ready for Eid, good weight and price"
                        style={{ fontSize: 16, borderRadius: 12 }}
                    />
                </Form.Item>

                <Form.Item
                    label={<span style={{ fontSize: 17, fontWeight: 600 }}>3️⃣ Pick a category</span>}
                    required
                >
                    <Select
                        value={selectedCategoryId || undefined}
                        onChange={onSelectCategory}
                        placeholder="Tap to choose a category"
                        size="large"
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: '100%', fontSize: 16 }}
                        optionLabelProp="label"
                    >
                        {categories.map((category) => (
                            <Select.Option
                                key={category.id}
                                value={category.id}
                                label={category.name}
                            >
                                <Space size="small">
                                    <span style={{ fontSize: 18 }} dangerouslySetInnerHTML={{ __html: category.icon }} />
                                    <span>{category.name}</span>
                                </Space>
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="start_time"
                    label={<span style={{ fontSize: 17, fontWeight: 600 }}>4️⃣ When does it start?</span>}
                    rules={[{ required: true, message: 'Please choose a start date and time' }]}
                >
                    <DatePicker
                        showTime
                        format="DD MMM YYYY, hh:mm A"
                        placeholder="Tap to choose date & time"
                        style={{ width: '100%', height: 52, fontSize: 16, borderRadius: 12 }}
                        disabledDate={disabledDate}
                        disabledTime={disabledTime}
                    />
                </Form.Item>

                <div style={{ marginBottom: 20 }}>
                    <button
                        type="button"
                        onClick={() => setShowMore((prev) => !prev)}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                            color: '#1890ff',
                            fontSize: 15,
                            fontWeight: 600
                        }}
                    >
                        {showMore ? <DownOutlined /> : <RightOutlined />}
                        More options (optional)
                    </button>

                    {showMore && (
                        <div style={{ marginTop: 16, padding: 16, background: '#fafafa', borderRadius: 12 }}>
                            <Form.Item
                                name="location"
                                label={
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>
                                        <EnvironmentOutlined /> Location
                                    </span>
                                }
                            >
                                <Input
                                    size="large"
                                    placeholder="e.g. Lahore, Punjab"
                                    style={{ fontSize: 16, height: 48, borderRadius: 12 }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="video_url"
                                label={
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>
                                        <PlayCircleOutlined /> Video Link
                                    </span>
                                }
                                rules={[{ validator: videoUrlValidator }]}
                            >
                                <Input
                                    size="large"
                                    placeholder="e.g. https://youtube.com/watch?v=..."
                                    style={{ fontSize: 16, height: 48, borderRadius: 12 }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>
                                        <PictureOutlined /> Auction Photos
                                    </span>
                                }
                                style={{ marginBottom: 0 }}
                            >
                                <SimpleImageDropzone files={photos} onChange={onPhotosChange} />
                            </Form.Item>
                        </div>
                    )}
                </div>

                <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    disabled={!selectedCategoryId}
                    size="large"
                    icon={<ArrowRightOutlined />}
                    style={{
                        height: 56,
                        fontSize: 18,
                        fontWeight: 700,
                        borderRadius: 14,
                        marginTop: 8,
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        border: 'none'
                    }}
                >
                    Next: Add Lots
                </Button>
            </Form>
        </div>
    );
};

export default WizardAuctionStep;
